import { useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { hashMatchesAnchor } from '@modules/dashboard/dashboardAnchors';

const SETTLE_MS = 1500;
const HIGHLIGHT_MS = 2200;
const INTERACTION_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'];

export const useScrollToAnchor = (anchor: string, ready: boolean) => {
  const { hash, key } = useLocation();
  const [highlighted, setHighlighted] = useState(false);
  const [hasBeenReady, setHasBeenReady] = useState(ready);
  const matches = hashMatchesAnchor(hash, anchor);

  useEffect(() => {
    if (ready) {
      setHasBeenReady(true);
    }
  }, [ready]);

  useEffect(() => {
    if (!matches || !hasBeenReady) {
      return undefined;
    }
    const element = document.getElementById(anchor);
    if (element == null) {
      return undefined;
    }

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    let interacted = false;
    const markInteracted = () => {
      interacted = true;
    };
    const scrollToElement = (behavior: ScrollBehavior) =>
      element.scrollIntoView({ behavior, block: 'start' });

    scrollToElement(reduceMotion ? 'auto' : 'smooth');
    setHighlighted(true);

    INTERACTION_EVENTS.forEach(name =>
      window.addEventListener(name, markInteracted, { passive: true }),
    );
    let isInitialObservation = true;
    const observer = new ResizeObserver(() => {
      if (isInitialObservation) {
        isInitialObservation = false;
        return;
      }
      if (!interacted) {
        scrollToElement('auto');
      }
    });
    observer.observe(element.parentElement ?? document.body);

    const stopWatching = () => {
      observer.disconnect();
      INTERACTION_EVENTS.forEach(name =>
        window.removeEventListener(name, markInteracted),
      );
    };

    const settleTimer = window.setTimeout(stopWatching, SETTLE_MS);
    const highlightTimer = window.setTimeout(
      () => setHighlighted(false),
      HIGHLIGHT_MS,
    );

    return () => {
      stopWatching();
      window.clearTimeout(settleTimer);
      window.clearTimeout(highlightTimer);
      setHighlighted(false);
    };
  }, [anchor, matches, hasBeenReady, key]);

  return { highlighted };
};
